import { encode, decode, type ClientAuth, type ClientMessage, type ServerMessage } from '@cratered/shared';

type Listener = (msg: ServerMessage) => void;

/** Typed websocket to the game server with automatic reconnect and re-hello. */
export class Net {
  private socket: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private statusListeners = new Set<(s: NetStatus) => void>();
  private retry = 0;
  private closed = false;
  status: NetStatus = 'connecting';
  /** The credentials of the last hello, for HTTP calls that must prove who they are (art uploads). */
  auth: ClientAuth | null = null;

  constructor(
    private readonly url: string,
    private readonly hello: () => { instanceId: string; auth: ClientAuth; avatarUrl?: string },
  ) {}

  connect(): void {
    this.closed = false;
    this.open();
  }

  private open(): void {
    this.setStatus('connecting');
    const ws = new WebSocket(this.url);
    this.socket = ws;
    ws.onopen = () => {
      this.retry = 0;
      this.setStatus('online');
      const h = this.hello();
      this.auth = h.auth;
      ws.send(encode({ t: 'hello', instanceId: h.instanceId, auth: h.auth, avatarUrl: h.avatarUrl }));
    };
    ws.onmessage = (ev) => {
      const msg = decode<ServerMessage>(String(ev.data));
      if (msg) for (const l of this.listeners) l(msg);
    };
    ws.onclose = () => {
      if (this.socket !== ws) return;
      this.socket = null;
      if (this.closed) return;
      this.setStatus('offline');
      const delay = Math.min(8000, 500 * 2 ** this.retry++);
      setTimeout(() => this.open(), delay);
    };
    ws.onerror = () => ws.close();
  }

  send(msg: ClientMessage): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(encode(msg));
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(cb: (s: NetStatus) => void): () => void {
    this.statusListeners.add(cb);
    return () => this.statusListeners.delete(cb);
  }

  private setStatus(s: NetStatus): void {
    this.status = s;
    for (const cb of this.statusListeners) cb(s);
  }

  close(): void {
    this.closed = true;
    this.socket?.close();
  }
}

export type NetStatus = 'connecting' | 'online' | 'offline';

export function wsUrl(apiBase: string): string {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}${apiBase}/ws`;
}
